import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";
import Firestore = firebase.firestore.Firestore;
import Auth = firebase.auth.Auth;
import {
  DocRef,
  getRepoRef,
  newDocRef,
  newRepoRef,
  Repo,
  RepoRef
} from "@/domain/repo";

export interface Bookmark {
  index: number;
  repo: RepoRef;
  doc: DocRef;
}

export function newBookmark(): Bookmark {
  return {
    index: 0,
    repo: newRepoRef(),
    doc: newDocRef()
  };
}

function debounce(func: any, wait: any) {
  let timeout: number | undefined;
  return async function(this: any, ...args: any) {
    const later = () => {
      timeout = undefined;
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export class BookmarkService {
  private firestore: Firestore;
  private auth: Auth;

  constructor() {
    this.firestore = firebase.firestore();
    this.auth = firebase.auth();
  }

  async getBookmarkList(): Promise<Bookmark[]> {
    const snapshot = await this.collection()
      .limit(10)
      .get();
    return snapshot.docs.map(doc => doc.data() as Bookmark);
  }

  async getBookmark(repoId: string): Promise<Bookmark> {
    const repoSnapshot = await this.document(repoId).get();
    const bookmark = Object.assign(
      newBookmark(),
      repoSnapshot.data() as Bookmark
    );
    return {
      ...bookmark,
      index: Math.max(0, bookmark.index)
    };
  }

  setIndex: (
    repoId: string,
    repo: Repo,
    doc: DocRef,
    index: number
  ) => Promise<void> = debounce(
    async (repoId: string, repo: Repo, doc: DocRef, index: number) => {
      const repoRef = getRepoRef(repoId, repo);
      const bookmark: Bookmark = {
        index: index,
        repo: repoRef,
        doc
      };
      await this.document(repoId).set(bookmark, {
        merge: true
      });
    },
    200
  );

  private collection() {
    const uid = this.auth.currentUser!!.uid;
    return this.firestore.collection(`users/${uid}/bookmarks`);
  }

  private document(repoId: string) {
    const uid = this.auth.currentUser!!.uid;
    return this.firestore.doc(`users/${uid}/bookmarks/${repoId}`);
  }
}
